import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
// @ts-ignore
import { sys } from '@netbasequid/theme'
import { WidgetId } from 'features/types'
import { Theme } from 'features/ui/types'
import { cloneDeep } from 'lodash'
import { useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { useDashboardWidgetData } from 'hooks/useWidgetState'
import { AnnotatedMetricResult } from 'schemas/AnnotatedMetricResultSchema'
import { MetricSeries } from 'schemas/MetricSeriesSchema'
import { DashboardWidgetData } from 'features/widget/types'
import { getLabel } from 'utils/measureTypeUtil'
import { getParentTypes } from 'utils/MetricItemUtil'
import MetricItemVo from 'vo/MetricItem'
import { MetricItem } from 'schemas/MetricItemSchema'
import MetricItemViz from 'components/visualization/MetricGrid/MetricItem'
import { ChartConfig } from 'components/visualization/MetricGrid/MetricItem/types/chartConfig'
import { useDisplayTheme } from 'hooks/useUiState'
import { AutomationId } from 'utils/automationUtil'
import ErrorMessage from 'components/share/ErrorMessage'

import { extractData, parseChart } from '../utils/DataHandler'
import { isProjectedTopic, getMetricDataset, isVocMetrics } from '../utils'
import { SummaryMetricsType } from '../../types'
import { VocMessageContent } from '../ErrorMessage'

const MetricGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(274px, 1fr));
  gap: 8px 0px;
`

const MetricItemContainer = styled.div`
  display: flex;
  justify-content: center;
`

const InfoContainer = styled.div<{ displayTheme: Theme }>`
  margin-top: 16px;
  color: ${({ displayTheme }) => sys[displayTheme].text.placeholder};
  font-size: ${sys.typescale[14].size};
`
/*
 ** When the second results is not delta or count, it is a composite search.
 ** deltaIdx: The search result index for delta result.
 ** countIdx: The search result index for count result. (MeasureType.MENTIONS)
 */
const checkIsCompositeSearch = (
  results: DashboardWidgetData<AnnotatedMetricResult>[],
  deltaIdx: number,
  countIdx: number
) => results[1] && deltaIdx !== 1 && countIdx !== 1

const SummaryMetricsWidget = ({ widgetId }: { widgetId: WidgetId }) => {
  const {
    measures,
    metricNames,
    viewOption,
    analysisType,
    selectedTopic
  } = useDashboardWidgetSetting<SummaryMetricsType>(widgetId)
  const { data, dataIdx } = useDashboardWidgetData<AnnotatedMetricResult>(widgetId)
  const theme = useDisplayTheme()
  const { deltaIdx, countIdx } = dataIdx!
  const [metricGrids, setMetricGrids] = useState<MetricItem[]>([])
  const [hasVocMetrics, setHasVocMetrics] = useState(false)
  const [vocMetricsOnly, setVocMetricsOnly] = useState(false)

  useEffect(() => {
    // Get all the parentTypes for given viewOption and analysisType
    const parentTypesByViewOption = getParentTypes(viewOption, analysisType)
    // Only include those measures that are selected (TODO: To include VoC using `metricNames`)
    const selectedMeasureTypes = parentTypesByViewOption.filter((type) => measures!.includes(type))
    // Creating metricItems with measreTypes
    const allMetricItems = selectedMeasureTypes.map((type) => MetricItemVo({ category: type }))
    const nonVocMetricItems = allMetricItems.filter((item) => !isVocMetrics(item.category!))
    setMetricGrids(nonVocMetricItems)
    setHasVocMetrics(allMetricItems.length - nonVocMetricItems.length > 0)
    setVocMetricsOnly(
      allMetricItems.length - nonVocMetricItems.length > 0 && nonVocMetricItems.length === 0
    )
  }, [viewOption, setMetricGrids, analysisType, measures])

  let idx = 0
  const fullResults = cloneDeep(data)
  const lastServiceResult = fullResults[0]

  const isCompositeSearch = checkIsCompositeSearch(data, deltaIdx, countIdx)
  if (isCompositeSearch) {
    lastServiceResult.results!.push(fullResults[idx + 1].results![0])
  }

  let metricSeries: MetricSeries[] = []
  // TODO: Voc related
  // let metricNameSeries: MetricSeries[] = []
  const hasMeasures = measures && measures.length > 0
  const hasMetricNames = metricNames && metricNames.length > 0

  if (hasMeasures) {
    metricSeries = getMetricDataset(lastServiceResult, idx)
    idx += 1
  } else if (hasMetricNames) {
    // TODO: Voc related
    // metricNameSeries = getMetricDataset(lastServiceResult, idx)
    idx += 1
  }
  // TODO: Can `isCompositeSearch` be replaced with (hasMeasures && hasMetricNames)
  if (isCompositeSearch) {
    // TODO: Voc related
    // metricNameSeries = getMetricDataset(lastServiceResult, idx)
    idx += 1
  }

  // Evaluate delta series.
  let deltaSeries: MetricSeries[] | null = null
  // TODO: Voc related
  // let deltaNameSeries: MetricSeries[] | null = null
  if (deltaIdx === idx) {
    if (hasMeasures) {
      deltaSeries = fullResults[idx] ? getMetricDataset(fullResults[idx], 0) : null
      idx += 1
    }
    if (hasMetricNames) {
      // TODO: Voc related
      // deltaNameSeries = getMetricDataset(fullResults[idx], 0)
      idx += 1
    }
  }

  if (countIdx > 0) {
    metricSeries = metricSeries.concat(getMetricDataset(fullResults[countIdx], 0))
    // Bugfix: https://jira.netbase.com/browse/CB-27880
    // when there is only metricNames, copy a fake mention measure search result
    // on index 1 from index 0 and point metricNamesSeries to it.
    if (!hasMeasures && hasMetricNames) {
      lastServiceResult.results![1] = cloneDeep(lastServiceResult.results![0])
      // TODO: VoC related
      // metricNameSeries = getMetricDataset(lastServiceResult, 1)
    }
    lastServiceResult.results![0].data.metrics![0].dataset = metricSeries
  }

  // TODO: VoC related
  // if (hasMeasures) {
  //   parseMetricSeries_(metricSeries);
  // }
  // if (hasMetricNames) {
  //   parseMetricSeries_(metricNameSeries);
  // }

  const metricPeriod =
    fullResults[0].results![0].data.endDate! - fullResults[0].results![0].data.startDate!

  const deltaPeriod =
    deltaIdx > 0
      ? fullResults[deltaIdx].results![0].data.endDate! -
        fullResults[deltaIdx].results![0].data.startDate!
      : null

  // When we don't have the entire pervious period, we don't show delta.
  const msPerDay = 86400000
  if (metricPeriod - deltaPeriod! > msPerDay) {
    deltaSeries = null
    // TODO: VoC related
    // deltaNameSeries = null
  }

  // TODO: VoC related
  // if (deltaSeries) {
  //   parseMetricSeries_(deltaSeries);
  // }
  // if (deltaNameSeries) {
  //   parseMetricSeries_(deltaNameSeries);
  // }

  // Converting the data to the format that the chart expects.
  const metricData: Record<string, Record<string, any>> = extractData(
    metricSeries,
    false,
    analysisType!,
    isProjectedTopic(selectedTopic)!,
    viewOption!
  )
  // TODO: VoC related
  // const metricNameData = extractData(
  //   metricNameSeries,
  //   false,
  //   analysisType!,
  //   isProjectedTopic(selectedTopic)!,
  //   viewOption!,
  //   true
  // )
  const deltaData = extractData(
    deltaSeries!,
    true,
    analysisType!,
    isProjectedTopic(selectedTopic)!,
    viewOption!
  )
  // TODO: VoC related
  // const deltaNameData = extractData(
  //   deltaNameSeries!,
  //   true,
  //   analysisType!,
  //   isProjectedTopic(selectedTopic)!,
  //   viewOption!,
  //   true
  // )

  const chartConfigList = [] as ChartConfig[]
  metricGrids.forEach((metricItem) => {
    const { category, metricName } = metricItem
    const seriesName = metricItem.metricName ? metricName : getLabel(category!, analysisType)

    // Derek's TODO: metricData not use display string as key, should use enum
    chartConfigList.push(
      parseChart(category!, seriesName!, metricData, deltaData, viewOption!, analysisType!)
    )
  })
  return vocMetricsOnly ? (
    <ErrorMessage>
      <VocMessageContent />
    </ErrorMessage>
  ) : (
    <>
      <MetricGridContainer data-testId={AutomationId.metricGrid.container}>
        {chartConfigList.map((chartConfig, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <MetricItemContainer key={index}>
            <MetricItemViz chartConfig={chartConfig} viewOption={viewOption!} />
          </MetricItemContainer>
        ))}
      </MetricGridContainer>
      {hasVocMetrics && (
        <InfoContainer displayTheme={theme} data-testid={AutomationId.message.voc}>
          <VocMessageContent />
        </InfoContainer>
      )}
    </>
  )
}
export default SummaryMetricsWidget
